/**
 * Question cycle grouping utility
 * Groups session messages by question cycle for structured display in ConversationPanel
 */

import type { Message } from '@/types/session';
import type { PebblesAward } from '@/types/pebbles';

/**
 * Represents a grouped question cycle
 * Contains all messages for a single question or intro section
 */
export interface QuestionCycle {
  /** Question number (null for intro/general messages) */
  questionNumber: number | null;
  /** Display label (e.g., 'Question 1 of 5' or 'Introduction') */
  label: string;
  /** All messages in this cycle, sorted by timestamp ascending */
  messages: Message[];
  /** True if cycle contains a question_complete message */
  isComplete: boolean;
  /** Pebbles award data from question_complete message, if present */
  pebbles: PebblesAward | null;
}

/**
 * Result of grouping messages by question cycle
 */
export interface QuestionCycleResult {
  /** Array of question cycles (intro first, then numbered questions in order) */
  cycles: QuestionCycle[];
  /** Total questions in the session (highest questionNumber found) */
  totalQuestions: number;
  /** Current question number (highest questionNumber with messages) */
  currentQuestion: number;
}

/**
 * Sort messages by timestamp in ascending order
 * Handles invalid timestamps gracefully by treating them as epoch 0
 * BUG-20260213-132402: Added role-based secondary sort for equal timestamps - 2026-02-13
 * When timestamps are equal, student messages sort before tutor messages
 *
 * @param messages - Array of messages to sort
 * @returns Sorted array of messages (shallow copy)
 */
function sortMessagesByTimestamp(messages: Message[]): Message[] {
  return [...messages].sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime() || 0;
    const timeB = new Date(b.timestamp).getTime() || 0;

    // Primary sort: timestamp ascending
    if (timeA !== timeB) {
      return timeA - timeB;
    }

    // Secondary sort: when timestamps are equal, student before tutor
    // BUG-20260213-132402: This handles existing data with identical timestamps
    const rolePriority = (role: string) => {
      if (role === 'student') return 0;
      if (role === 'tutor') return 1;
      return 2; // system, passage, or other roles
    };

    return rolePriority(a.role) - rolePriority(b.role);
  });
}

/**
 * Extract pebbles award data from question_complete message in a cycle
 *
 * @param messages - Messages in the cycle
 * @returns PebblesAward object if found, null otherwise
 */
function extractPebblesData(messages: Message[]): PebblesAward | null {
  const completeMessage = messages.find(
    (msg) => msg.decisionType === 'question_complete' && msg.basePebbles !== undefined
  );

  if (!completeMessage) {
    return null;
  }

  return {
    basePebbles: completeMessage.basePebbles!,
    bonusAwarded: completeMessage.bonusAwarded ?? 0,
    totalAwarded: completeMessage.totalAwarded ?? 0,
    updatedTotalPebbles: completeMessage.updatedTotalPebbles ?? 0,
  };
}

/**
 * Group messages by question cycle using questionNumber field
 * Returns cycles array with intro cycle first (if present), then numbered cycles in order
 *
 * @param messages - Array of Message objects from useSessionMessages hook
 * @returns QuestionCycleResult with grouped cycles and metadata
 *
 * @example
 * ```typescript
 * const { cycles, totalQuestions, currentQuestion } = groupMessagesByQuestionCycle(messages);
 * cycles.forEach(cycle => {
 *   console.log(cycle.label, cycle.messages.length);
 * });
 * ```
 */
export function groupMessagesByQuestionCycle(
  messages: Message[] | undefined | null,
  sessionTotalQuestions?: number
): QuestionCycleResult {
  // Handle empty/null input
  if (!messages || messages.length === 0) {
    return {
      cycles: [],
      totalQuestions: 0,
      currentQuestion: 0,
    };
  }

  // Group messages by questionNumber (null for intro)
  const messageGroups = new Map<number | null, Message[]>();

  for (const message of messages) {
    const key =
      message.questionNumber !== undefined && message.questionNumber !== null
        ? message.questionNumber
        : null;

    if (!messageGroups.has(key)) {
      messageGroups.set(key, []);
    }
    messageGroups.get(key)!.push(message);
  }

  // Calculate total questions: use session-level totalQuestions if available, otherwise infer from max questionNumber
  const numericKeys = Array.from(messageGroups.keys()).filter((k) => k !== null) as number[];
  const inferredTotal = numericKeys.length > 0 ? Math.max(...numericKeys) : 0;
  const totalQuestions =
    sessionTotalQuestions && sessionTotalQuestions > inferredTotal
      ? sessionTotalQuestions
      : inferredTotal;

  // Calculate current question (highest questionNumber with messages)
  const currentQuestion = totalQuestions;

  // Build cycles array
  const cycles: QuestionCycle[] = [];

  // Add intro cycle first if it exists
  if (messageGroups.has(null)) {
    const introMessages = messageGroups.get(null)!;
    cycles.push({
      questionNumber: null,
      label: 'Introduction',
      messages: sortMessagesByTimestamp(introMessages),
      isComplete: true, // Intro messages are always considered complete
      pebbles: null,
    });
  }

  // Add numbered question cycles in ascending order
  const sortedNumericKeys = numericKeys.sort((a, b) => a - b);
  for (const questionNumber of sortedNumericKeys) {
    const cycleMessages = messageGroups.get(questionNumber)!;
    const hasComplete = cycleMessages.some((msg) => msg.decisionType === 'question_complete');

    cycles.push({
      questionNumber,
      label: `Question ${questionNumber} of ${totalQuestions}`,
      messages: sortMessagesByTimestamp(cycleMessages),
      isComplete: hasComplete,
      pebbles: extractPebblesData(cycleMessages),
    });
  }

  return {
    cycles,
    totalQuestions,
    currentQuestion,
  };
}
